/* External */
import { useRef, useEffect } from "react";
import { useSelector } from "react-redux";

/* Assets */
import rokuLogo from "./../assets/images/roku-logo.svg";

/* Others */
import { SELF_CARE } from "./urls";

export const DEFAULT_VALUES = {
  LANGUAGE: "es",
  SUBTITLES: null,
  AUDIO: null,
  COUNTRY: "co"
};

export const HOME_BATCH_SIZE = 2;

export const IMAGE_WIDTH = {
  CARD_2X3: 128
};

export const LANDERS_URIS = {
  HOME: "HOME_MOBILE"
};

export const LANGUAGES = {
  ANY_LANGUAGE: null,
  DEFAULT_LANGUAGE: null,
  ENGLISH_LANGUAGE: "en",
  PORTUGUESE_LANGUAGE: "pt",
  SPANISH_LANGUAGE: "es"
};

export const PATHS = {
  HOME: "/home",
  MY_LIST: "/home/myList",
  SEARCH: "/search"
};

export const PDP_ASSET_TYPES = {
  live: "live",
  movies: "movies",
  series: "series",
  vod: "vod"
};

export const PROVIDER_FR = "forgerock";

export const RESOURCE_TYPES_ORBIS = {
  MOVIE: "movie",
  STATION: "station",
  STATIONS: "stations",
  EPG_STATIONS: "epg/stations",
  EPG_MOVIES: "epg/movies",
  EPG_EVENTS: "epg/events",
  EPG_COMPETITIONS: "epg/competitions",
  EPG_TEAMCOMPETITIONS: "epg/teamCompetitions",
  EPISODES: "epg/episodes",
  SHOW_EPISODE: "showEpisode",
  VOD_EPISODE: "vod/episode",
  VOD_EPISODES: "vod/episodes",
  VOD_MOVIES: "vod/movies",
  VOD_EVENTS: "vod/events",
  VOD_COMPETITIONS: "vod/competitions",
  VOD_TEAM_COMPETITIONS: "vod/teamCompetitions",
  SHOWS: "shows",
  SHOW: "show",
  BANNERS: "banners"
};

export const RESOURCE_TYPES_SEARCH_RESULTS = {
  VOD_MOVIE: "searchResults/vod/movie",
  VOD_SERIE: "searchResults/vod/serie",
  VOD_EVENT: "searchResults/vod/event",
  LIVE_MOVIE: "searchResults/live/movie",
  LIVE_SERIE: "searchResults/live/serie",
  LIVE_EVENT: "searchResults/live/event",
  LIVE_STATION: "searchResults/live/station"
};

export const SERVICES_CLIENT_ID = "web-mobile";

export const STATUS_TYPES = {
  UNLOAD_STATE: "notLoaded",
  INIT_STATE: "initState",
  SUCCEED_STATE: "succeedState",
  FAILED_STATE: "failedState"
};

export const TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;
export const UNFINISHED_STATUS_TYPES = [STATUS_TYPES.UNLOAD_STATE, STATUS_TYPES.INIT_STATE];

/**
 * @method getBooleanValueFromString
 * @description Method to get boolean value from a string value.
 * @param {string} booleanStr value string of boolean.
 * @return {boolean}
 */
export function getBooleanValueFromString(booleanStr = "") {
  if (booleanStr) {
    return "true" === booleanStr.toLowerCase().trim() || "1" === booleanStr.toLowerCase().trim();
  }
  return false;
}

/**
 * Method for build an image with a specific size and also builds initial path when hasn't http or https
 * @param {string} image
 * @param {object} resizer
 */
export function getImageUrl(image = "", resizer = "") {
  const { REACT_APP_RESIZER_HOST = "", REACT_APP_RESIZER_HOST_DTV = "" } = process.env;
  let cloudImage;

  if (!(REACT_APP_RESIZER_HOST_DTV && REACT_APP_RESIZER_HOST))
    console.error("No realm id or resizer host constant defined for image resizer.");

  if (!image) return;

  const { width = 0 } = resizer || {};

  if (width) {
    cloudImage = `${REACT_APP_RESIZER_HOST}w_${sizeByBreakpoint(width)},c_limit/${REACT_APP_RESIZER_HOST_DTV}/${image}`;
  } else {
    cloudImage = `${REACT_APP_RESIZER_HOST}c_limit/${REACT_APP_RESIZER_HOST_DTV}/${image}`;
  }
  return cloudImage;
}

/**
 * @method generateRandomString
 * @description Generates a random string
 * @return {string} Random string
 */
export const generateRandomString = () => (+new Date() + Math.random() * 100).toString(32);

/**
 * @method checkNetworkBlackout
 * @description Method to get boolean value if it finds blackout value for a country.
 * @param {object} strings
 * @return {boolean}
 */
export function checkNetworkBlackout(labels, iso2Country) {
  return !!(
    navigator?.connection?.type === "cellular" &&
    labels.find((label) => label.toLowerCase() === `mobile_blackout_${iso2Country.toLowerCase()}`)
  );
}

/**
 * @method isEpisode
 * @description Method to get boolean value from a string resourceType.
 * @param {string} resourceType.
 * @return {boolean}
 */

export const isEpisode = (resourceType) =>
  [
    RESOURCE_TYPES_ORBIS.SHOW,
    RESOURCE_TYPES_ORBIS.SHOW_EPISODE,
    RESOURCE_TYPES_ORBIS.SHOWS,
    RESOURCE_TYPES_ORBIS.VOD_EPISODE,
    RESOURCE_TYPES_ORBIS.VOD_EPISODES
  ].includes(resourceType);

/**
 * @method isValidString
 * @description Validates if a string is different from undefined, null and empty
 * @param {string} value
 */
export function isValidString(value) {
  if (value === undefined || value === null || value === "") {
    return false;
  }
  return true;
}

export function pixelRatio(size) {
  let scale = 1;
  if (window.devicePixelRatio !== undefined) scale = window.devicePixelRatio;
  return size * scale;
}

export function sizeByBreakpoint(size) {
  const pageWidth = document.body.clientWidth;
  const proportion = size / pageWidth;
  const roundValue = pixelRatio(100);
  const resize = (breakpoint) => {
    return proportion * breakpoint;
  };
  let resized;

  // common breakpoints
  switch (true) {
    case pageWidth <= 500:
      resized = resize(500);
      break;
    case pageWidth <= 700:
      resized = resize(700);
      break;
    case pageWidth <= 1000:
      resized = resize(1000);
      break;
    case pageWidth <= 1300:
      resized = resize(1300);
      break;
    case pageWidth <= 1600:
      resized = resize(1600);
      break;
    case pageWidth <= 2000:
      resized = resize(2000);
      break;
    default:
      resized = size;
  }

  return Math.ceil(Math.floor(resized) / roundValue) * roundValue;
}

/**
 * @description Creates an observer and returns a ref to observe a node
 */
export function useIntersectionObserver(
  observerCallback = () => {},
  observerConfig = {},
  updateObserve = null,
  disconnectObserver = null
) {
  const ref = useRef(null);
  const observer = useRef(
    new window.IntersectionObserver((entries) => {
      observerCallback(entries);
    }, observerConfig)
  );

  /**
   * @description Makes observer observe ref
   * @cleanUp Disconnect observer
   */
  useEffect(() => {
    const { current: currentObserver } = observer;

    if (currentObserver && ref.current) currentObserver.observe(ref.current);

    return () => currentObserver.disconnect();
  }, [observer, ref, updateObserve]);

  /**
   * @description Disconnects observer
   * @cleanUp Disconnect observer
   */
  useEffect(() => {
    if (disconnectObserver) {
      const { current: currentObserver } = observer;
      currentObserver.disconnect();
    }
  }, [disconnectObserver]);

  return [ref];
}

export const usePrevious = (value) => {
  const ref = useRef();

  /**
   * @description Updates ref
   */
  useEffect(() => {
    ref.current = value;
  }, [value]);

  return ref.current;
};

export const SECTIONS_URIS = {
  CONTINUE_WATCHING: "SIGUEVIENDO",
  CONTINUE_WATCHING_TA: "UCTAContinueWatching",
  CONTINUE_WATCHING_LIVE: "SIGUEVIENDOLIVE",
  MY_LIST: "MILISTA"
};

export const useIsChromecastConnected = () => {
  const connectionState = useSelector((state) => state.chromecast.connectionState);

  return connectionState === window.cast?.framework?.CastState?.CONNECTED;
};

export const ROKU_PAY_USERS_MSO_PROVIDER = "roku";

/**
 * @method getRokuPayUsersOverScreenModalData
 * @description Returns the data to show on the Over Screen Modal for Roku Pay Users.
 */
export const getRokuPayUsersOverScreenModalData = (formatMessage) => {
  return {
    headerImage: rokuLogo,
    description: formatMessage({
      id: "roku.modal.description",
      defaultMessage: "Para modificar tu suscripción, favor de acceder a su cuenta a través de Roku."
    }),
    mainButtonText: formatMessage({
      id: "roku.modal.mainButtonText",
      defaultMessage: "Ir a Roku"
    }),
    mainButtonHref: SELF_CARE.roku,
    secondaryButtonText: formatMessage({
      id: "roku.modal.secondaryButtonText",
      defaultMessage: "Cancelar"
    }),
    openHrefInBlank: false,
    showCloseIcon: true
  };
};

/**
 * @method getSelfCareHrefTarget
 * @description Returns whether the SelfCare redirect should open on the same tab
 *              or a new one depending on the MSO Provider attribute.
 */
export const getSelfCareHrefTarget = (msoProvider) => {
  switch (msoProvider) {
    case ROKU_PAY_USERS_MSO_PROVIDER:
      return "_self";
    default:
      return "_blank";
  }
};

export const SEARCH_KEYWORD_THRESHOLD = 3;

export const BLIM_SEARCH_SOURCE = 2;
